import { Button, makeStyles, TextField } from '@material-ui/core';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@material-ui/lab';
import { useBuyers, useMaterials } from 'hooks';
import { DEFAULT_ITEMS_PER_SELECT } from 'tableFilterContext';
import { Buyer, Material, StockConditionneRepreneurFilters } from 'types';
import { transformObjectId } from 'services/apiPlatformTransformer';
import NextIcon from 'components/Icons/NextIcon';
import { colors } from 'theme';

const useStyles = makeStyles({
  form: {
    width: '100%',
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  fieldLine: {
    marginTop: 8,
    width: '25%',
    margin: '1vw',
    '& label': {
      color: '#949FAC',
    },

    '& > div': {
      width: '100%',
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  searchButton: {
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  searchButtonIcon: {
    marginLeft: '10px',
    width: 17,
    height: 17,
  },
  message: {
    fontWeight: 600,
    textAlign: 'center',
    fontFamily: '"Gill Sans", "Helvetica", "Arial", sans-serif',
    color: '#FF9500',
  },
  textField: {
    '& fieldset': {
      borderColor: colors.neutralGray,
    },
  },
});

export interface FilterStockConditionneRepreneurFormProps {
  filters: StockConditionneRepreneurFilters;
  onSubmit: SubmitHandler<StockConditionneRepreneurFilters>;
  dateForMessage: Date;
}

const FilterStockConditionneRepreneurForm: React.FC<FilterStockConditionneRepreneurFormProps> = ({
  filters,
  onSubmit,
  dateForMessage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'stockConditionneParRepreneurPage.form' });
  const [buyers] = useBuyers({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const defaultValues = {
    buyerId: filters?.buyerId ?? '',
    materialId: filters?.materialId ?? '',
  };

  const { control, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues,
  });

  return (
    <>
      <form id="conditioned_stocks_by_buyer_filter_form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <div className={classes.fieldLine}>
          <span>
            <Controller
              name="buyerId"
              control={control}
              render={({ field: { ref, onChange, ...fieldProps } }) => (
                <Autocomplete
                  options={buyers ?? []}
                  size="small"
                  getOptionLabel={buyer => buyer.name}
                  noOptionsText={t('noResults')}
                  onChange={(_e, value: Buyer | null) => {
                    onChange({ target: { value: transformObjectId(value) } });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      data-qa-id="buyer-filter"
                      className={classes.textField}
                      {...fieldProps}
                      placeholder={t('filters.buyer')}
                      variant="outlined"
                    />
                  )}
                />
              )}
            />
          </span>
        </div>
        <div className={classes.fieldLine}>
          <span>
            <Controller
              name="materialId"
              control={control}
              render={({ field: { ref, onChange, ...fieldProps } }) => (
                <Autocomplete
                  options={materials ?? []}
                  size="small"
                  getOptionLabel={material => material.shortName}
                  noOptionsText={t('noResults')}
                  onChange={(_e, value: Material | null) => {
                    onChange({ target: { value: transformObjectId(value) } });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      data-qa-id="material-filter"
                      className={classes.textField}
                      {...fieldProps}
                      placeholder={t('filters.material')}
                      variant="outlined"
                    />
                  )}
                />
              )}
            />
          </span>
        </div>
        <div className={classes.fieldLine}>
          <p className={classes.message}>
            {t('filters.message', {
              monthInLetter: dateForMessage.toLocaleString('default', { month: 'long' }),
              year: dateForMessage.getFullYear(),
            })}
          </p>
        </div>
        <div className={classes.fieldLine}>
          <Button style={{ marginTop: 5 }} size="small" type="submit" variant="text">
            <div className={classes.searchButton}>{t('searchButton')}</div>
            <NextIcon className={classes.searchButtonIcon} />
          </Button>
        </div>
      </form>
    </>
  );
};

export default FilterStockConditionneRepreneurForm;
